import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';

import {
  AddMcpCheckRecord,
  CheckSheetActionTypes,
  DeleteMcpCheckRecord,
  LoadCurrentCsaSuccess,
  LoadCurrentMcpCsa,
  McpActionTypes,
  McpAddCheckedTag,
  McpDeleteCSA,
  McpMoveCSA,
  McpScopeOfWork,
  PackageCommonActionTypes,
  McpScopeOfWorkSuccess,
  UpdateMcpCsa,
  UpdateMcpCsItemStatus,
  UpdateMcpRecordValue,
  UpdateCsItemStatusFailure,
  UpdateCsItemStatusSuccess,
  FetchMcpSignaturesFromHistory,
  UpdateSignaturesSuccess,
  SignMcpCsaSigntureEntry,
  InsertAndSignMcpCsaSigntureEntry,
  UploadMcpCsaFile,
  DeleteMcpCsaAttachment,
  FetchMcItemStatusesFromHistory,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCsaId, getCurrentMcpId, getCurrentProject } from '@completion/selectors';
import { McpService } from '@completion/services';
import { UserPreferenceService } from '../../services/user-preference.service';
import { Project } from '@completion/models';
import { UserPreference } from '@completion/enums';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class McpCheckSheetEffect {
  constructor(
    private actions$: Actions,
    private mcpService: McpService,
    private readonly userPreferenceService: UserPreferenceService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadMcpScopeOfWork$ = createEffect(() =>
    this.actions$.pipe(
      ofType<McpScopeOfWork>(PackageCommonActionTypes.McpScopeOfWork),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentMcpId) as Observable<number>),
      filter(([_, currentProject, mcpId]) => !!(currentProject && mcpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'getCheckedTags',
        serviceArgs: ([_, currentProject, mcpId]) => [
          currentProject.id,
          mcpId,
          this.userPreferenceService.isEnabled(UserPreference.USE_FLAT_TAG_TABLE_VIEW),
        ],
        onSuccess: (response) => new McpScopeOfWorkSuccess(response as any),
      }),
    ),
  );

  addCheckedTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType<McpAddCheckedTag>(McpActionTypes.McpAddCheckedTag),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentMcpId) as Observable<number>),
      filter(([_, currentProject, mcpId]) => !!(currentProject && mcpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'addCheckedTags',
        serviceArgs: ([action, currentProject, mcpId]) => [currentProject.id, mcpId, action.checkSheetAssignment],
        onSuccess: () => ({ type: '[Success] Add Checked Tags' }),
      }),
    ),
  );

  deleteCheckSheetAssignment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<McpDeleteCSA>(McpActionTypes.McpDeleteCSA),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentMcpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'deleteMcpCheckSheetAssignment',
        serviceArgs: ([action, currentProject, mcpId]) => [currentProject.id, mcpId, action.ids],
        onSuccess: () => ({ type: '[Success] Delete Check Sheet Assignment' }),
      }),
    ),
  );

  moveTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType<McpMoveCSA>(McpActionTypes.McpMoveCSA),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentMcpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'moveTags',
        serviceArgs: ([action, currentProject, mcpId]) => [currentProject.id, mcpId, action.csas],
        onSuccess: () => ({ type: '[Success] Move Tags' }),
      }),
    ),
  );

  loadMcpCsa$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCurrentMcpCsa>(CheckSheetActionTypes.LoadCurrentMcpCsa),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'getCheckSheetAssignment',
        serviceArgs: ([_, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  updateMcpCsItemStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateMcpCsItemStatus>(CheckSheetActionTypes.UpdateMcpCsItemStatus),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, project, mcpId, csaId]) => !!(project && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'updateMcCheckListItem',
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId, action.item],
        onSuccess: (csa: any, sourceArgs) => {
          const action = Array.isArray(sourceArgs?.[0]) ? sourceArgs[0][0] : sourceArgs?.[0];
          const itemNumber: number = action?.item?.number;
          const updatedItem = (csa?.csItems ?? []).find((i: any) => i.number === itemNumber) ?? action?.item;
          return new UpdateCsItemStatusSuccess(itemNumber, updatedItem);
        },
        onError: (err, sourceArgs) => {
          const action = Array.isArray(sourceArgs?.[0]) ? sourceArgs[0][0] : sourceArgs?.[0];
          const msg = err?.error?.message || err?.error?.error || err?.message || 'Unknown error';
          return new UpdateCsItemStatusFailure(action?.item?.number, msg);
        },
      }),
    ),
  );

  updateMcpCsa$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateMcpCsa>(CheckSheetActionTypes.UpdateMcpCsa),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentMcpId) as Observable<number>),
      filter(([_, currentProject, mcpId]) => !!(currentProject && mcpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'updateMcCheckSheetAssignment',
        serviceArgs: ([action, currentProject, mcpId]) => [currentProject.id, mcpId, action.csa, action.doSignOrVerify],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  updateMcpRecordValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateMcpRecordValue>(CheckSheetActionTypes.UpdateMcpRecordValue),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'updateMcpRecordValue',
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId, action.csItemNumber, action.recordValue],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  addMcpRecordValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddMcpCheckRecord>(CheckSheetActionTypes.AddMcpCheckRecord),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'addMcpRecordValue',
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [
          currentProject.id,
          mcpId,
          csaId,
          action.csItem.number,
          action.recordValue,
        ],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  deleteMcpRecordValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteMcpCheckRecord>(CheckSheetActionTypes.DeleteMcpCheckRecord),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'deleteMcpRecordValue',
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId, action.csItemNumber, action.recordValue],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  FetchMcpSignaturesFromHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchMcpSignaturesFromHistory>(CheckSheetActionTypes.FetchMcpSignaturesFromHistory),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'fetchMcpSignaturesFromHistory',
        serviceArgs: ([_, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId],
        onSuccess: (response) => new UpdateSignaturesSuccess(response as any),
      }),
    ),
  );

  FetchMcItemStatusesFromHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchMcItemStatusesFromHistory>(CheckSheetActionTypes.FetchMcItemstatusesFromHistory),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'fetchMcItemStatusesFromHistory',
        serviceArgs: ([_, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId],
        onSuccess: (response) => new UpdateSignaturesSuccess(response as any),
      }),
    ),
  );

  SignMcpCsaSigntureEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SignMcpCsaSigntureEntry>(McpActionTypes.SignMcpCsaSigntureEntry),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'signMcpCsaSigntureEntry',
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId, action.entry.id],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  InsertAndSignMcpCsaSigntureEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType<InsertAndSignMcpCsaSigntureEntry>(McpActionTypes.InsertAndSignMcpCsaSigntureEntry),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'insertAndSignMcpCsaSigntureEntry',
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [
          currentProject.id,
          mcpId,
          csaId,
          true,
          [action.entry],
          action.unsignedEntries,
        ],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  uploadMcpCsaFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UploadMcpCsaFile>(CheckSheetActionTypes.UploadMcpCsaFile),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'uploadMcpCsaFile',
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId, action.file],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  deleteMcpCsaAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteMcpCsaAttachment>(CheckSheetActionTypes.DeleteMcpCsaAttachment),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, mcpId, csaId]) => !!(currentProject && mcpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.mcpService,
        method: 'deleteMcpCsaAttachment',
        serviceArgs: ([action, currentProject, mcpId, csaId]) => [currentProject.id, mcpId, csaId, action.id],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );
}
